import authNative, {
  FirebaseAuthTypes,
} from "@react-native-firebase/auth";
import firestoreNative from "@react-native-firebase/firestore";
import { GoogleSignin } from "@react-native-google-signin/google-signin";

import {
  startPhoneVerification,
} from "./phoneVerification.native";

// GoogleSignin error codes returned as string constants. Kept inline instead
// of importing statusCodes because the shape has drifted across versions.
const GOOGLE_SIGN_IN_CANCELLED = "SIGN_IN_CANCELLED";
const GOOGLE_SIGN_IN_IN_PROGRESS = "IN_PROGRESS";

export type AccountLinkingErrorCode =
  | "NO_SIGNED_IN_USER"
  | "CREDENTIAL_ALREADY_IN_USE"
  | "PROVIDER_ALREADY_LINKED"
  | "REQUIRES_RECENT_LOGIN"
  | "USER_CANCELLED"
  | "POPUP_BLOCKED"
  | "INVALID_CREDENTIAL"
  | "ONLY_LINKED_PROVIDER"
  | "NETWORK_ERROR"
  | "UNKNOWN";

export class AccountLinkingError extends Error {
  code: AccountLinkingErrorCode;
  cause?: unknown;
  constructor(
    code: AccountLinkingErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = "AccountLinkingError";
    this.code = code;
    this.cause = cause;
  }
}

export type LinkPhoneConfirmation = {
  confirm: (code: string) => Promise<void>;
};

export type LinkPhoneParts = {
  prefix: string;
  number: string;
};

const setEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

export const mapLinkError = (error: unknown): AccountLinkingError => {
  if (error instanceof AccountLinkingError) return error;
  const err = error as { code?: string; message?: string };
  const code = err?.code;

  if (code === GOOGLE_SIGN_IN_CANCELLED) {
    return new AccountLinkingError(
      "USER_CANCELLED",
      "User cancelled.",
      error,
    );
  }
  if (code === GOOGLE_SIGN_IN_IN_PROGRESS) {
    return new AccountLinkingError(
      "USER_CANCELLED",
      "Google sign-in already in progress.",
      error,
    );
  }

  switch (code) {
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
    case "auth/email-already-in-use":
      return new AccountLinkingError(
        "CREDENTIAL_ALREADY_IN_USE",
        "That sign-in method is already used by another account.",
        error,
      );
    case "auth/provider-already-linked":
      return new AccountLinkingError(
        "PROVIDER_ALREADY_LINKED",
        "This sign-in method is already linked to your account.",
        error,
      );
    case "auth/requires-recent-login":
      return new AccountLinkingError(
        "REQUIRES_RECENT_LOGIN",
        "Recent sign-in required.",
        error,
      );
    case "auth/invalid-credential":
      return new AccountLinkingError(
        "INVALID_CREDENTIAL",
        "Invalid credential.",
        error,
      );
    case "auth/network-request-failed":
      return new AccountLinkingError(
        "NETWORK_ERROR",
        "Network error.",
        error,
      );
    default:
      return new AccountLinkingError(
        "UNKNOWN",
        "Failed to link account.",
        error,
      );
  }
};

export const getLinkedProviders = (
  user: FirebaseAuthTypes.User,
): string[] => {
  return user.providerData.map((p) => p.providerId);
};

export const syncProvidersToFirestore = async (
  user: FirebaseAuthTypes.User,
): Promise<{ updated: boolean; providers: string[] }> => {
  const providers = getLinkedProviders(user);
  const userDocRef = firestoreNative().collection("users").doc(user.uid);
  const snap = await userDocRef.get();
  if (!snap.exists()) {
    return { updated: false, providers };
  }
  const existing = (snap.data() as { providers?: string[] } | undefined)
    ?.providers ?? [];
  if (setEqual(existing, providers)) {
    return { updated: false, providers };
  }
  await userDocRef.update({ providers });
  return { updated: true, providers };
};

// Self-heals users/{uid}.phoneNumber.verified for accounts where Firebase Auth
// clearly has the phone provider linked but Firestore doesn't reflect it.
// The mismatch happens when a phone-auth flow authenticated the user but the
// follow-up Firestore write was skipped or interrupted (e.g. the old
// login.web.tsx code path that ignored phonePrefix/phoneNumber, or a signup
// interrupted between ensureUserDocExists and the phoneNumber merge). Both the
// PhoneVerifyBanner and the Settings → WhatsApp toggle gate on
// `hasPhoneProvider && phoneNumber.verified === true`, so leaving the mismatch
// in place keeps the banner up and the toggle disabled with no in-app recovery
// affordance. Idempotent: no-op when the provider is absent or Firestore
// already reflects verified:true.
export const syncPhoneVerifiedToFirestore = async (
  user: FirebaseAuthTypes.User,
): Promise<{ updated: boolean }> => {
  const hasPhoneProvider = user.providerData.some(
    (p) => p?.providerId === "phone",
  );
  if (!hasPhoneProvider) return { updated: false };

  const userDocRef = firestoreNative().collection("users").doc(user.uid);
  const snap = await userDocRef.get();
  if (!snap.exists()) return { updated: false };

  const existing = snap.data() as
    | {
        phoneNumber?: {
          number?: string | null;
          prefix?: string | null;
          verified?: boolean;
        };
      }
    | undefined;
  if (existing?.phoneNumber?.verified === true) {
    return { updated: false };
  }

  await userDocRef.update({
    phoneNumber: {
      verified: true,
      number: existing?.phoneNumber?.number ?? user.phoneNumber ?? null,
      prefix: existing?.phoneNumber?.prefix ?? null,
    },
  });
  return { updated: true };
};

const requireCurrentUser = (): FirebaseAuthTypes.User => {
  const current = authNative().currentUser;
  if (!current) {
    throw new AccountLinkingError(
      "NO_SIGNED_IN_USER",
      "Must be signed in to link a provider.",
    );
  }
  return current;
};

export const linkGoogleProvider = async (
  user: FirebaseAuthTypes.User,
): Promise<void> => {
  const target = user ?? requireCurrentUser();

  try {
    await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
    // Reset any prior session so the account chooser always shows.
    try {
      await GoogleSignin.signOut();
    } catch {
      // ignore
    }
    const signInResult = await GoogleSignin.signIn();
    const anyResult = signInResult as any;
    const idToken = anyResult?.data?.idToken ?? anyResult?.idToken;

    if (!idToken) {
      throw new AccountLinkingError(
        "INVALID_CREDENTIAL",
        "Google sign-in returned no token.",
      );
    }

    const googleCredential =
      authNative.GoogleAuthProvider.credential(idToken);
    await target.linkWithCredential(googleCredential);
  } catch (error) {
    throw mapLinkError(error);
  }

  await target.reload();
  await syncProvidersToFirestore(target);
};

export const linkEmailProvider = async (
  user: FirebaseAuthTypes.User,
  email: string,
  password: string,
): Promise<void> => {
  const target = user ?? requireCurrentUser();
  try {
    const credential = authNative.EmailAuthProvider.credential(
      email,
      password,
    );
    await target.linkWithCredential(credential);
  } catch (error) {
    throw mapLinkError(error);
  }

  // Linking a password credential resets `emailVerified` to false even when the
  // user was previously verified via Google. Without a verification email the
  // user gets bounced to the verify-email screen on the next sign-in — kick off
  // the mail here. Swallow send failures: the link is already committed and the
  // user can re-request from the verify screen.
  try {
    await target.sendEmailVerification();
  } catch {
    // ignore
  }

  await target.reload();
  await syncProvidersToFirestore(target);
};

export const linkPhoneProvider = async (
  user: FirebaseAuthTypes.User,
  phoneE164: string,
  phoneParts?: LinkPhoneParts,
): Promise<LinkPhoneConfirmation> => {
  const target = user ?? requireCurrentUser();

  const inner = await startPhoneVerification(phoneE164);
  return {
    confirm: async (code: string) => {
      await inner.confirm(code);

      // Mirror the phone into the Firestore user doc so `groupInvite` (which
      // queries by `phoneNumber.prefix` + `phoneNumber.number`) keeps working.
      // WhatsApp notifications are NOT auto-enabled here — the user opts in
      // explicitly from Settings → Notifications, gated by the consent modal.
      if (phoneParts) {
        const userDocRef = firestoreNative()
          .collection("users")
          .doc(target.uid);
        const snap = await userDocRef.get();
        if (snap.exists()) {
          await userDocRef.set(
            {
              phoneNumber: {
                prefix: phoneParts.prefix,
                number: phoneParts.number,
                verified: true,
              },
            },
            { merge: true },
          );
        }
      }

      await target.reload();
      await syncProvidersToFirestore(target);
    },
  };
};

export const unlinkProvider = async (
  user: FirebaseAuthTypes.User,
  providerId: string,
): Promise<void> => {
  const target = user ?? requireCurrentUser();

  const currentProviders = getLinkedProviders(target);
  if (!currentProviders.includes(providerId)) {
    return;
  }
  if (currentProviders.length <= 1) {
    throw new AccountLinkingError(
      "ONLY_LINKED_PROVIDER",
      "Cannot unlink the only sign-in method.",
    );
  }

  try {
    await target.unlink(providerId);
  } catch (error) {
    throw mapLinkError(error);
  }

  await target.reload();

  if (providerId === "phone") {
    const userDocRef = firestoreNative().collection("users").doc(target.uid);
    const snap = await userDocRef.get();
    if (snap.exists()) {
      const existing = snap.data() as {
        phoneNumber?: {
          number?: string | null;
          prefix?: string | null;
          verified?: boolean;
        };
      };
      if (existing?.phoneNumber) {
        // Auto-disable WhatsApp notifications: the WA send channel requires a
        // verified phone, so symmetrize with the opt-in that happens on
        // linkPhoneProvider. Leave the monthly quota / usage / plan-start
        // untouched so re-verifying restores the same cycle.
        await userDocRef.update({
          phoneNumber: {
            ...existing.phoneNumber,
            verified: false,
          },
          waPostNotificationsEnabled: false,
        });
      }
    }
  }

  await syncProvidersToFirestore(target);
};
